<template>
  <div class="farm-settings">
    <v-card class="settings-card" elevation="0">
      <!-- Header with BACK button -->
      <div class="d-flex justify-end mb-6">
        <v-btn
          class="text-none"
          height="36"
          color="primary"
          outlined
          @click="handleBack"
        >
          <span class="text-caption font-weight-medium">BACK</span>
        </v-btn>
      </div>

      <!-- Main Content -->
      <div v-if="$store.state.loading" class="text-center pa-10">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
        <div class="mt-4">Loading farm settings...</div>
      </div>
      <div v-else class="settings-content">
        <h2 class="section-title mb-4">{{ this.$t("setting.farmSettings") || "Farm Settings" }}</h2>
        <p class="text-body-1 mb-6">
          Configure farm size ranges for your organization.
        </p>

        <v-form
          ref="farmSettingsForm"
          v-model="valid"
          lazy-validation
          autocomplete="off"
        >
          <v-card class="pa-6" outlined>
            <v-card-text>
              <v-row>
                <!-- Small Farm -->
                <div class="col-12 mb-4">
                  <div class="d-flex flex-wrap justify-space-between align-center ga-2 mb-2">
                    <label
                      for="smallFarmFrom"
                      class="text-subtitle-1 font-weight-bold"
                    >
                      {{ this.$t("setting.smallFarm") }}
                    </label>
                    <v-checkbox
                      v-model="editedSettings.smallFarm.isInclusive"
                      :label="this.$t('setting.inclusive')"
                      dense
                      class="mt-0"
                      hide-details="auto"
                    />
                  </div>
                  <div class="d-flex flex-wrap justify-space-between align-center ga-2">
                    <v-text-field
                      v-model="editedSettings.smallFarm.from"
                      type="number"
                      outlined
                      dense
                      hide-details="auto"
                      :rules="rules.smallFarm.from"
                      style="flex: 1; max-width: 200px;"
                    />
                    <div class="mx-2">to</div>
                    <v-text-field
                      v-model="editedSettings.smallFarm.to"
                      type="number"
                      outlined
                      dense
                      hide-details="auto"
                      :rules="rules.smallFarm.to"
                      style="flex: 1; max-width: 200px;"
                    />
                  </div>
                </div>

                <!-- Medium Farm -->
                <div class="col-12 mb-4">
                  <div class="d-flex flex-wrap justify-space-between align-center ga-2 mb-2">
                    <label
                      for="mediumFarmFrom"
                      class="text-subtitle-1 font-weight-bold"
                    >
                      {{ this.$t("setting.mediumFarm") }}
                    </label>
                    <v-checkbox
                      v-model="editedSettings.mediumFarm.isInclusive"
                      :label="this.$t('setting.inclusive')"
                      dense
                      class="mt-0"
                      hide-details="auto"
                    />
                  </div>
                  <div class="d-flex flex-wrap justify-space-between align-center ga-2">
                    <v-text-field
                      v-model="editedSettings.mediumFarm.from"
                      type="number"
                      outlined
                      dense
                      hide-details="auto"
                      :rules="rules.mediumFarm.from"
                      style="flex: 1; max-width: 200px;"
                    />
                    <div class="mx-2">to</div>
                    <v-text-field
                      v-model="editedSettings.mediumFarm.to"
                      type="number"
                      outlined
                      dense
                      hide-details="auto"
                      :rules="rules.mediumFarm.to"
                      style="flex: 1; max-width: 200px;"
                    />
                  </div>
                </div>

                <!-- Large Farm -->
                <div class="col-lg-6 col-md-6 col-sm-12 col-xs-12">
                  <label
                    for="largeFarm"
                    class="text-subtitle-1 font-weight-bold"
                  >
                    {{ this.$t("setting.largeFarm") }}
                  </label>
                  <v-text-field
                    v-model="editedSettings.largeFarm"
                    type="number"
                    outlined
                    dense
                    hide-details="auto"
                    :rules="rules.largeFarm"
                    append-icon="mdi-plus"
                  />
                </div>
              </v-row>
            </v-card-text>

            <v-card-actions class="mt-4">
              <v-spacer></v-spacer>
              <v-btn
                @click="handleBack"
                outlined
                height="44"
                width="116"
                depressed
                color="primary"
              >
                {{ this.$t("cancel") }}
              </v-btn>
              <v-btn
                @click="updateSettings"
                height="44"
                width="116"
                color="primary"
                :loading="saving"
              >
                {{ this.$t("permissions.update") }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-form>
      </div>
    </v-card>
  </div>
</template>

<script>
import loadingMixin from "@/mixins/LoadingMixin";
import PermissionsService from "@/_services/PermissionsService";

export default {
  name: 'FarmSettings',
  mixins: [loadingMixin],
  data() {
    return {
      saving: false,
      valid: true,
      editedSettings: {
        smallFarm: {
          to: 0,
          from: 0,
          isInclusive: false
        },
        mediumFarm: {
          to: 0,
          from: 0,
          isInclusive: false
        },
        largeFarm: 0
      },
      requiredMessage: this.$t("setting.requiredMessage") || "This field is required",
      nonNegativeMessage: this.$t("setting.nonNegativeMessage") || "Value must be non-negative",
      wholeNumMessage: this.$t("setting.wholeNumMessage") || "Value must be a whole number",
      fieldToGreaterThanFrom: this.$t("setting.fieldToGreaterThanFrom") || "To value must be greater than From value",
      fieldToGreaterThanFromMedium: this.$t("setting.fieldToGreaterThanFromMedium") || "Medium Farm From must be greater than Small Farm To",
      fieldToGreaterThanMediumTo: this.$t("setting.fieldToGreaterThanMediumTo") || "Large Farm must be greater than Medium Farm To",
      rules: {
        smallFarm: {
          from: [
            (v) => !!v || this.requiredMessage,
            (v) => Number(v) >= 0 || this.nonNegativeMessage,
            (v) => /^\d+$/.test(v) || this.wholeNumMessage
          ],
          to: [
            (v) => !!v || this.requiredMessage,
            (v) => Number(v) >= 0 || this.nonNegativeMessage,
            (v) => Number(v) > Number(this.editedSettings.smallFarm.from) || this.fieldToGreaterThanFrom,
            (v) => /^\d+$/.test(v) || this.wholeNumMessage
          ],
        },
        mediumFarm: {
          from: [
            (v) => !!v || this.requiredMessage,
            (v) => Number(v) >= 0 || this.nonNegativeMessage,
            (v) => Number(v) > Number(this.editedSettings.smallFarm.to) || this.fieldToGreaterThanFromMedium,
            (v) => /^\d+$/.test(v) || this.wholeNumMessage
          ],
          to: [
            (v) => !!v || this.requiredMessage,
            (v) => Number(v) >= 0 || this.nonNegativeMessage,
            (v) => Number(v) > Number(this.editedSettings.mediumFarm.from) || this.fieldToGreaterThanFrom,
            (v) => /^\d+$/.test(v) || this.wholeNumMessage
          ],
        },
        largeFarm: [
          (v) => !!v || this.requiredMessage,
          (v) => Number(v) >= 0 || this.nonNegativeMessage,
          (v) => v > Number(this.editedSettings.mediumFarm.to) || this.fieldToGreaterThanMediumTo,
          (v) => /^\d+$/.test(v) || this.wholeNumMessage
        ],
      },
    };
  },
  async mounted() {
    await this.fetchFarmSettings();
  },
  methods: {
    async fetchFarmSettings() {
      this.startLoading();
      try {
        const response = await PermissionsService.getGlobalSettings();
        if (response.success && response.data && response.data.globalSetting) {
          const globalSetting = response.data.globalSetting;
          
          // Load farm settings
          this.editedSettings = {
            smallFarm: globalSetting.smallFarm || {
              to: 0,
              from: 0,
              isInclusive: false
            },
            mediumFarm: globalSetting.mediumFarm || {
              to: 0,
              from: 0,
              isInclusive: false
            },
            largeFarm: globalSetting.largeFarm || 0
          };
        }
      } catch (error) {
        console.error('Error fetching farm settings:', error);
        this.$notify({
          title: 'Failed to load farm settings',
          type: 'error',
        });
      } finally {
        this.stopLoading();
      }
    },
    handleBack() {
      this.$router.push({ name: 'enterpriseGeneralSettings' });
    },
    async updateSettings() {
      if (!this.$refs.farmSettingsForm.validate()) return;
      
      this.saving = true;
      try {
        // Prepare farm settings data
        const farmSettingsData = {
          smallFarm: {
            from: Number(this.editedSettings.smallFarm.from),
            to: Number(this.editedSettings.smallFarm.to),
            isInclusive: this.editedSettings.smallFarm.isInclusive
          },
          mediumFarm: {
            from: Number(this.editedSettings.mediumFarm.from),
            to: Number(this.editedSettings.mediumFarm.to),
            isInclusive: this.editedSettings.mediumFarm.isInclusive
          },
          largeFarm: Number(this.editedSettings.largeFarm)
        };

        const response = await PermissionsService.updateGlobalSettings(farmSettingsData);
        
        if (response.success) {
          this.$notify({
            title: this.$t("profileSetting.settingsUpdated") || 'Settings saved successfully',
            type: 'success',
          });
          // Refresh the data
          await this.fetchFarmSettings();
        } else {
          throw new Error(response.message || 'Failed to save settings');
        }
      } catch (error) {
        console.error('Error saving farm settings:', error);
        this.$notify({
          title: this.$t("profileSetting.settingsNotUpdated") || 'Failed to save settings',
          text: error.message || 'An error occurred while saving settings',
          type: 'error',
        });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.farm-settings {
  .settings-card {
    padding: 24px;
  }

  .settings-content {
    max-width: 1200px;
    margin: 0 auto;
  }

  .section-title {
    font-size: 24px;
    font-weight: 600;
    color: #333;
  }

  .ga-2 {
    gap: 1rem;
  }

  ::v-deep .v-messages__message {
    line-height: 14px;
  }
}
</style>
